import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import BlockContent from '@sanity/block-content-to-react'

import {
  SanityFilter,
  SanityFilterGroup,
  SanitySort,
  SanitySortOptionType,
} from '@data/sanity/queries/types/modules'
import { SanityProductFragment } from '@data/sanity/queries/types/product'
import { HasCollectionStrings } from '@data/sanity/queries/types/site'
import {
  clampRange,
  compareNumbers,
  compareStrings,
  getAllCombinations,
  parseOptionalParameter,
} from '@lib/helpers'
import { UrlParameter, usePrevious, useUrlParameters } from '@lib/hooks'
import { Filter } from '@lib/product'
import { SiteContext } from '@lib/site'
import { StringsContext } from '@lib/strings'

import Button from '@components/buttons/button'
import ProductCard from '@blocks/shop/product-card'
import CollectionFilter from './filter'
import CollectionFilterChips from './filter-chips'
import CollectionSort from './sort'

interface CollectionProps extends HasCollectionStrings {
  products: SanityProductFragment[]
  featuredProductIds: number[]
  paginationLimit: number
  title?: string
  sort?: SanitySort
  filter?: SanityFilter
}

/**
 * Returns a method that compares products for sorting by price.
 */
const sortProductsByPrice = (isDescending = false) => {
  const sign = isDescending ? -1 : 1

  return (
    { price: price1 }: SanityProductFragment,
    { price: price2 }: SanityProductFragment
  ) => sign * compareNumbers(price1, price2)
}

/**
 * Returns a method that compares products for sorting by title.
 */
const sortProductsByTitle = (isDescending = false) => {
  const sign = isDescending ? -1 : 1

  return (
    { title: title1 }: SanityProductFragment,
    { title: title2 }: SanityProductFragment
  ) => sign * compareStrings(title1, title2)
}

/**
 * Returns a method that compares products for sorting by creation date.
 */
const sortProductsByDate = (isDescending = false) => {
  const sign = isDescending ? -1 : 1

  return (
    { createdAt: createdAt1 }: SanityProductFragment,
    { createdAt: createdAt2 }: SanityProductFragment
  ) => sign * compareStrings(createdAt1, createdAt2)
}

/**
 * Collection filter and sort hook.
 */
const useFilterAndSort = (
  products: SanityProductFragment[],
  featuredProductIds: number[],
  filters: Filter[],
  sortValue: SanitySortOptionType | null
) => {
  const filteredProducts = useMemo(() => {
    // Get all combinations of all filter values
    const filterCombinations = getAllCombinations(
      ...filters
        .filter(({ values }) => values.length > 0)
        .map(({ values }) => values)
    )

    return products.filter((product) => {
      const productFilterValues = product.filters?.map(({ slug }) => slug) ?? []

      return filterCombinations.some((values) =>
        values.every((value) => productFilterValues.includes(value))
      )
    })
  }, [filters, products])

  switch (sortValue) {
    case SanitySortOptionType.PRICE_ASCENDING: {
      return filteredProducts.sort(sortProductsByPrice())
    }

    case SanitySortOptionType.PRICE_DESCENDING: {
      return filteredProducts.sort(sortProductsByPrice(true))
    }

    case SanitySortOptionType.ALPHA_ASCENDING: {
      return filteredProducts.sort(sortProductsByTitle())
    }

    case SanitySortOptionType.ALPHA_DESCENDING: {
      return filteredProducts.sort(sortProductsByTitle(true))
    }

    case SanitySortOptionType.DATE_ASCENDING: {
      return filteredProducts.sort(sortProductsByDate())
    }

    case SanitySortOptionType.DATE_DESCENDING: {
      return filteredProducts.sort(sortProductsByDate(true))
    }

    case SanitySortOptionType.FEATURED: {
      const productIndices = featuredProductIds.reduce(
        (result: Record<number, number>, value: number, index: number) => (
          (result[value] = index + 1), result
        ),
        {}
      )

      return filteredProducts.sort(
        (product1: SanityProductFragment, product2: SanityProductFragment) =>
          (productIndices[product1.id] ?? Infinity) -
          (productIndices[product2.id] ?? Infinity)
      )
    }

    default: {
      return filteredProducts
    }
  }
}

/**
 * Gets sort parameter value from URL parameters.
 */
const getSortParameterValue = (activeParameters: UrlParameter[]) => {
  const sortParameter = activeParameters.find(({ name }) => name === 'sort')
  const value = sortParameter?.value

  if (!value || Array.isArray(value)) {
    return null
  }

  return value as SanitySortOptionType
}

/**
 * Returns a method that gets filter from a URL parameter.
 */
const getFilterFromUrlParameter =
  (filterGroups: SanityFilterGroup[]) =>
  (activeParameter: UrlParameter): Filter => {
    const filterGroup = filterGroups.find(
      ({ slug }) => slug === activeParameter.name
    )
    const optionSlugs = filterGroup?.options?.map(({ slug }) => slug) ?? []
    const values =
      activeParameter.value && Array.isArray(activeParameter.value)
        ? activeParameter.value
        : activeParameter.value?.split(',') ?? []

    return {
      name: activeParameter.name,
      values: [
        ...new Set(
          values.filter((value) => value && optionSlugs.includes(value))
        ),
      ],
    }
  }

/**
 * Returns a method that gets updated URL parameter from given new parameters.
 */
const getUpdatedParameter =
  (newParameters: UrlParameter[]) =>
  (parameter: UrlParameter): UrlParameter => {
    const matchedParameter = newParameters.find(
      ({ name }) => name === parameter.name
    )

    if (!matchedParameter) {
      return parameter
    }

    return {
      ...parameter,
      value: matchedParameter?.value ?? null,
    }
  }

/**
 * The collection component.
 */
const Collection = ({
  collectionStrings,
  products,
  featuredProductIds,
  paginationLimit,
  title,
  sort,
  filter,
}: CollectionProps) => {
  const { isPageTransition } = useContext(SiteContext)
  const strings = useContext(StringsContext)

  const [hasPagination, setHasPagination] = useState(
    paginationLimit > 0 && products.length > paginationLimit
  )
  const [currentCount, setCurrentCount] = useState(
    hasPagination ? paginationLimit : products.length
  )

  const collectionItems = useRef<(HTMLDivElement | null)[]>([])
  const filterGroups = filter?.groups ?? []

  // Manage URL parameters
  const [currentParameters, setCurrentParameters] = useUrlParameters([
    { name: 'page', value: null },
    { name: 'sort', value: sort?.options?.[0]?.type ?? null },
    ...filterGroups.map(({ slug }) => ({ name: slug, value: null })),
  ])
  const previousParameters = usePrevious(currentParameters)
  const activeParameters =
    isPageTransition && previousParameters
      ? previousParameters
      : currentParameters

  // Manage sorting value
  const activeSortValue = getSortParameterValue(activeParameters)

  // Manage filter values
  const activeFilters = activeParameters
    .filter(({ name }) => !['page', 'sort'].includes(name))
    .map(getFilterFromUrlParameter(filterGroups))
  const activeFilterValueCount = activeFilters.reduce(
    (total, { values }) => total + values.length,
    0
  )

  // Manage sorting and pagination
  const processedProducts = useFilterAndSort(
    products,
    featuredProductIds,
    activeFilters,
    activeSortValue
  )
  const paginatedProducts = [...processedProducts.slice(0, currentCount)]

  // Manage product pagination
  useEffect(() => {
    const pageParameter = activeParameters.find(({ name }) => name === 'page')
    const page = Number(parseOptionalParameter(pageParameter?.value) ?? 1)
    const productCount = processedProducts.length
    const pageCount = Math.ceil(productCount / paginationLimit)

    if (page <= 1 || page > pageCount) {
      return
    }

    const newCount = clampRange(paginationLimit * page, 1, productCount)

    if (newCount !== currentCount) {
      setCurrentCount(newCount)
    }
  }, [currentCount, processedProducts, activeParameters, paginationLimit])

  // Focus on the first link in the first or last product card
  useEffect(() => {
    const pageProductIndex =
      currentCount < processedProducts.length
        ? currentCount - paginationLimit
        : processedProducts.length - 1
    collectionItems.current[pageProductIndex]
      ?.querySelector<HTMLAnchorElement>('[href]')
      ?.focus({ preventScroll: true })
  }, [currentCount, paginationLimit, processedProducts.length])

  // Update pagination status
  useEffect(
    () => setHasPagination(currentCount < processedProducts.length),
    [currentCount, processedProducts.length]
  )

  // Handle filter and sort updates
  const updateParameters = useCallback(
    (newParameters: UrlParameter[]) =>
      setCurrentParameters(
        activeParameters.map(getUpdatedParameter(newParameters))
      ),
    [activeParameters, setCurrentParameters]
  )

  // Handle loading more products
  const loadMore = useCallback(() => {
    const newCount = clampRange(
      currentCount + paginationLimit,
      1,
      processedProducts.length
    )

    const newPage = Math.ceil(newCount / paginationLimit)

    setCurrentCount(newCount)
    updateParameters([
      { name: 'page', value: `${newPage > 1 ? `${newPage}` : null}` },
    ])
  }, [currentCount, processedProducts, paginationLimit, updateParameters])

  const clearFilters = () =>
    updateParameters(activeFilters.map(({ name }) => ({ name, value: null })))

  if (products.length === 0) {
    return null
  }

  return (
    <section>
      <div className="sticky top-[var(--headerHeight)] z-10 bg-pageBG bg-opacity-95">
        <div className="container flex justify-between">
          {filter?.isActive && (
            <CollectionFilter
              collectionStrings={collectionStrings}
              filterGroups={filterGroups}
              activeFilters={activeFilters}
              activeFilterValueCount={activeFilterValueCount}
              itemTotal={processedProducts.length}
              onChange={updateParameters}
            />
          )}

          {sort?.isActive && (
            <CollectionSort
              collectionStrings={collectionStrings}
              sortOptions={sort.options ?? []}
              activeSortValue={activeSortValue}
              onChange={updateParameters}
            />
          )}
        </div>
      </div>

      {filter?.isActive && (
        <div className="bg-pageText">
          <div className="container">
            <CollectionFilterChips
              collectionStrings={collectionStrings}
              filterGroups={filterGroups}
              activeFilters={activeFilters}
              activeFilterValueCount={activeFilterValueCount}
              onClick={updateParameters}
            />
          </div>
        </div>
      )}

      <div className="container relative z-0 overflow-hidden">
        <div
          className={cx('grid grid-cols-1 md:grid-cols-3 gap-x-10 -m-px', {
            'grid-cols-1': processedProducts.length === 0,
          })}
        >
          {paginatedProducts.map((product, index) => (
            <ProductCard
              ref={(node) => (collectionItems.current[index] = node)}
              key={
                product.id +
                activeParameters
                  .filter((activeParam) => activeParam.name !== 'page')
                  .map((activeParam) => activeParam.value)
                  .filter(Boolean)
                  .join('-')
              }
              product={product}
              activeFilters={activeFilters}
              hasVisuals={!!product.photos.main || !!product.photos.listing}
              showGallery={
                product.useGallery === 'true' && !!product.photos.main
              }
              showThumbnails={
                product.useGallery === 'false' && !!product.photos.listing
              }
              showOption={!!product.surfaceOption}
              showPrice
              showQuickAdd
              isInline
            />
          ))}
        </div>

        {processedProducts.length === 0 && (
          <div className="px-8 py-16 min-h-[35vh] text-center">
            <BlockContent
              blocks={collectionStrings.collectionFilterNoResults}
            />
            <Button secondary onClick={clearFilters} className="mt-8">
              {collectionStrings.collectionClearFiltersLabel}
            </Button>
          </div>
        )}

        {hasPagination && (
          <div className="relative py-4 sm:py-8 text-center">
            <Button primary onClick={loadMore}>
              {strings.buttonLoadMore}
              <span className="sr-only">
                {collectionStrings.collectionProducts?.replace(
                  /{title}/gi,
                  title ? `&quot;${title}&quot;` : ''
                )}
              </span>
            </Button>
          </div>
        )}

        {processedProducts.length > 0 && (
          <div className="relative py-4 sm:py-8 text-center">
            <p aria-live="polite" role="status" aria-atomic="true">
              {collectionStrings.collectionProductCount
                ?.replace(/{count}/gi, `${paginatedProducts.length}`)
                ?.replace(/{total}/gi, `${processedProducts.length}`)}
            </p>
          </div>
        )}
      </div>
    </section>
  )
}

export default Collection
